import { useState } from 'react';
import {
  X,
  Mail,
  Printer,
  Download,
  Radio,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Send
} from 'lucide-react';
import { useApi } from '../contexts/ApiContext';

export default function SendLineupModal({ isOpen, onClose, raceId, raceName, lineupCount }) {
  const api = useApi();
  const [activeTab, setActiveTab] = useState('email');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState(`${raceName} - Lineup`);
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  if (!isOpen) return null;

  const handleSendEmail = async () => {
    if (!email) {
      setResult({ type: 'error', message: 'Please enter an email address' });
      return;
    }

    setSending(true);
    setResult(null);

    try {
      const response = await api.races.sendLineupEmail(raceId, email, subject);
      if (response.success) {
        setResult({ type: 'success', message: `Lineup sent to ${email}` });
        setEmail('');
      } else {
        setResult({ type: 'error', message: response.message || 'Failed to send email' });
      }
    } catch (err) {
      setResult({ type: 'error', message: 'Failed to send email' });
    } finally {
      setSending(false);
    }
  };

  const handleExportCSV = () => {
    const url = api.races.getLineupExportURL(raceId, 'csv');
    window.open(url, '_blank');
  };

  const handlePrint = () => {
    const url = api.races.getLineupExportURL(raceId, 'print');
    window.open(url, '_blank');
  };

  const handleSendToLTS = async () => {
    setSending(true);
    setResult(null);

    try {
      const response = await api.races.sendLineupToLTS(raceId);
      if (response.success) {
        setResult({
          type: 'success',
          message: `Lineup pushed to LTS (${response.data?.competitor_count || lineupCount} competitors)`
        });
      } else {
        setResult({ type: 'error', message: response.message || 'Failed to push to LTS' });
      }
    } catch (err) {
      setResult({ type: 'error', message: 'Failed to push to LTS' });
    } finally {
      setSending(false);
    }
  };

  const tabs = [
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'print', label: 'Print', icon: Printer },
    { id: 'export', label: 'Export', icon: Download },
    { id: 'lts', label: 'LTS', icon: Radio }
  ];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      <div className="relative bg-white rounded-lg border border-slate-200 w-full max-w-md overflow-hidden shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-800">Send Lineup</h3>
            <p className="text-sm text-slate-500">{lineupCount} driver{lineupCount !== 1 ? 's' : ''} in lineup</p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setResult(null); }}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-2.5 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-wrg-blue-600 border-b-2 border-wrg-blue-600 bg-wrg-blue-50'
                  : 'text-slate-600 hover:text-slate-800 hover:bg-slate-50'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4">
          {/* Result Message */}
          {result && (
            <div className={`mb-4 p-3 rounded flex items-center gap-2 ${
              result.type === 'success'
                ? 'bg-emerald-50 border border-emerald-200 text-emerald-700'
                : 'bg-red-50 border border-red-200 text-red-700'
            }`}>
              {result.type === 'success' ? (
                <CheckCircle2 className="w-4 h-4 flex-shrink-0" />
              ) : (
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
              )}
              <span className="text-sm">{result.message}</span>
            </div>
          )}

          {/* Email Tab */}
          {activeTab === 'email' && (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Recipient Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="timing@mylaps.com"
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded text-sm focus:outline-none focus:border-wrg-blue-500 focus:ring-1 focus:ring-wrg-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Subject
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-3 py-2 bg-white border border-slate-300 rounded text-sm focus:outline-none focus:border-wrg-blue-500 focus:ring-1 focus:ring-wrg-blue-500"
                />
              </div>
              <button
                onClick={handleSendEmail}
                disabled={sending || !email}
                className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-wrg-blue-600 text-white rounded hover:bg-wrg-blue-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
              >
                {sending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Send Email
                  </>
                )}
              </button>
              <p className="text-xs text-slate-500 text-center">
                Lineup will be sent as an HTML table with all driver and transponder info
              </p>
            </div>
          )}

          {/* Print Tab */}
          {activeTab === 'print' && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                <Printer className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <h4 className="font-medium text-slate-800 mb-1">Print Lineup Sheet</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Opens a print-friendly view with positions, car numbers, drivers, and transponders
                </p>
                <button
                  onClick={handlePrint}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-wrg-blue-600 text-white rounded hover:bg-wrg-blue-700 transition-colors"
                >
                  <Printer className="w-4 h-4" />
                  Open Print View
                </button>
              </div>
            </div>
          )}

          {/* Export Tab */}
          {activeTab === 'export' && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                <Download className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <h4 className="font-medium text-slate-800 mb-1">Export to CSV</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Download lineup as a CSV file for Excel or timing software import
                </p>
                <button
                  onClick={handleExportCSV}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded hover:bg-emerald-700 transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Download CSV
                </button>
              </div>
            </div>
          )}

          {/* LTS Tab */}
          {activeTab === 'lts' && (
            <div className="space-y-4">
              <div className="p-4 bg-slate-50 rounded-lg border border-slate-200 text-center">
                <Radio className="w-10 h-10 text-slate-400 mx-auto mb-3" />
                <h4 className="font-medium text-slate-800 mb-1">Push to Live Timing</h4>
                <p className="text-sm text-slate-500 mb-4">
                  Send lineup directly to the WRG Live Timing System for real-time race tracking
                </p>
                <button
                  onClick={handleSendToLTS}
                  disabled={sending}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-slate-300 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Pushing...
                    </>
                  ) : (
                    <>
                      <Radio className="w-4 h-4" />
                      Push to LTS
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
